import React from 'react';
import PropTypes from 'prop-types';
import { Icon } from '..';

import './TaskTracker.scss';

/**
 * TaskTracker - A reusable timeline component for tracking tasks/items
 * @param {Array} items - Array of items to track
 * @param {Function} renderContent - Render prop for custom content card
 * @param {Function} getStatus - Function to determine item status (returns 'completed' | 'in_progress' | 'pending')
 * @param {Function} getKey - Function to get unique key for each item
 * @param {string} title - Optional header title
 * @param {boolean} showProgress - Whether to show progress bar header
 * @param {string} emptyTitle - Title for empty state
 * @param {string} emptyMessage - Message for empty state
 */
export const TaskTracker = ({
  items = [],
  renderContent,
  getStatus,
  getKey = (item, index) => item.id || index,
  title = 'Progress',
  showProgress = true,
  emptyTitle = 'No Tasks',
  emptyMessage = 'No tasks to display.',
}) => {
  // Process items with derived status
  const processedItems = items.map((item, index) => {
    let derivedStatus;

    if (getStatus) {
      // Use custom status function if provided
      derivedStatus = getStatus(item, index, items);
    } else {
      // Default status logic based on is_completed field
      if (item.is_completed) {
        derivedStatus = 'completed';
      } else {
        const prevItem = items[index - 1];
        if (!prevItem || prevItem.is_completed) {
          derivedStatus = 'in_progress';
        } else {
          derivedStatus = 'pending';
        }
      }
    }

    return { ...item, derivedStatus };
  });

  const completedCount = processedItems.filter(
    (item) => item.derivedStatus === 'completed'
  ).length;
  const progressPercent =
    items.length > 0 ? Math.round((completedCount / items.length) * 100) : 0;

  if (!items || items.length === 0) {
    return (
      <div className='task-tracker-empty'>
        <div className='empty-content'>
          <Icon name='Document' />
          <h3>{emptyTitle}</h3>
          <p>{emptyMessage}</p>
        </div>
      </div>
    );
  }

  return (
    <div className='task-tracker'>
      {showProgress && (
        <div className='tracker-header'>
          <div className='header-info'>
            <h3>{title}</h3>
            <span className='progress-text'>{progressPercent}% Complete</span>
          </div>
          <div className='progress-bar-container'>
            <div
              className='progress-fill'
              style={{ width: `${progressPercent}%` }}
            />
          </div>
        </div>
      )}

      <div className='timeline-container'>
        {processedItems.map((item, index) => (
          <div
            key={getKey(item, index)}
            className={`timeline-item ${item.derivedStatus}`}
          >
            <div className='timeline-marker'>
              <div className='marker-dot'>
                {item.derivedStatus === 'completed' && (
                  <Icon name='Check' size={12} />
                )}
                {item.derivedStatus === 'in_progress' && (
                  <div className='pulse-dot' />
                )}
              </div>
              <div className='marker-line' />
            </div>

            <div className='timeline-content'>
              {renderContent ? (
                renderContent(item, index, item.derivedStatus)
              ) : (
                <div>Render content not provided</div>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

TaskTracker.propTypes = {
  items: PropTypes.array.isRequired,
  renderContent: PropTypes.func,
  getStatus: PropTypes.func,
  getKey: PropTypes.func,
  title: PropTypes.string,
  showProgress: PropTypes.bool,
  emptyTitle: PropTypes.string,
  emptyMessage: PropTypes.string,
};
